import type { StudioView } from '../../utils/studioViewHistory'

/**
 * The shared bones of the studio navigation.
 *
 * **StudioNav.tsx was split into four files on 15 Oct 2026** — this, the
 * laptop row, the menu sheet and the thumb bar. It had grown to roughly 20KB
 * and the artifact editor silently truncates a whole-file write well below
 * that, so the navigation had become the one part of Portal OS that could
 * not be changed at all. Two of Tom's requests sat unbuildable behind that.
 * Keep every one of these files small enough to rewrite in one go.
 */

/**
 * The Crew Directory (formerly "Freelance Database", renamed 8 Sep 2026)
 * lives in its own Magic Patterns design — a separate app entirely, not part
 * of this codebase. "Linking" it means exactly that: an outbound link.
 */
export const CREW_DIRECTORY_URL = 'https://project-portal-crew-directory-502.magicpatterns.app'

/**
 * ONE LINE, AND NOTHING IN A MENU. Tom, 26 Sep, in three goes: *"make sure
 * all tabs fit in one line"*, then *"Need wrap up and team to be tabs on the
 * same line - I guess smaller?"*, then *"No I want crew directory on the
 * same line - nothing hidden."* And again on 15 Oct: *"adding more things
 * within things is harder to find for anyone using for the first time."*
 *
 * **So room comes out of the type, never out of the contents.** Twice I
 * bought space by moving something into the account menu and twice that was
 * the wrong trade: a tab you cannot see is a tab nobody uses.
 *
 * Where the room came from, measured rather than guessed — ten buttons at
 * 10px with 0.22em tracking came to roughly 1140px against the 1100px the
 * row has at the container's 1180px maximum:
 *
 * - **Tracking 0.22em → 0.14em** (`tracking-wide2`), about 70px at ~86
 *   characters, and the only lever with that much in it.
 * - **Wordmark h-6 → h-5** on the row, another 30-odd px.
 * - px-2.5 and gap-1.5.
 *
 * **If an eleventh view is ever added, do not shave the type again.** The
 * next notch down stops reading as capitals. Either the row becomes two
 * deliberate tiers, or something genuinely leaves it — a decision for Tom,
 * not one to solve in CSS.
 */
export const NAV_BUTTON =
  'inline-flex h-9 shrink-0 items-center whitespace-nowrap border px-2.5 text-[10px] uppercase leading-none tracking-wide2 transition-colors duration-150 ease-out'
export const NAV_IDLE = 'border-paper/30 text-paper/70 hover:border-paper hover:text-paper'
export const NAV_ACTIVE = 'border-paper text-paper'

/**
 * **The one control on the row that MAKES something (15 Oct 2026).** Tom:
 * *"Add new client tab is better at the top — feels special."* So it is
 * filled rather than outlined, and it is the only filled thing up there.
 * Everything else on the row is a place you go; this is the thing you do.
 */
export const NAV_MAKE =
  'inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap border border-paper bg-paper px-3.5 text-[10px] uppercase leading-none tracking-wide2 text-ink transition-colors duration-150 ease-out hover:bg-transparent hover:text-paper'

export const MENU_ITEM =
  'label block w-full px-5 py-4 text-left text-paper/60 transition-colors duration-150 ease-out hover:text-paper'

/**
 * WHO SEES DISPATCHES. Owners only — Tom and Zeena. *"Dispatches is only for
 * Zeena and me (owners) you can remove from other staff and future staff."*
 *
 * **Hiding it in the interface secures nothing.** A hidden button is still a
 * function call anyone can make from a console. The actual locks are
 * `is_portal_owner()` on every conversation_* RPC and the matching check
 * inside the ask-dispatches Edge Function. If you ever find yourself relying
 * on a nav file to keep the studio's correspondence private, stop and go and
 * look at those two.
 */

/**
 * The four things worth a thumb (24 Sep 2026 — rewritten 27 Sep 2026).
 *
 * Tom, 27 Sep: *"for the tabs at the bottom can they be WEEK NEWS DISPATCHES
 * CALENDAR, the rest can be in the menu."*
 *
 * That replaced the set the old comment argued for — Week, Calendar, Call
 * Sheets, Clients — on the reasoning that the four thumb tabs were the
 * shoot-day tools. **They are not; they are the checking-in tools.** A call
 * sheet and a client are things you open once, on purpose. Do not restore
 * the old four on the strength of the old note.
 *
 * **Dispatches is owners only, so Shilpa and Sreyangshi get three tabs.** No
 * fourth was substituted in for them: a different app under the same thumb
 * would be a worse surprise than a shorter bar.
 *
 * **Wrap-ups is deliberately not a fifth thumb tab**: five tabs at 390px is
 * 78px each, and a report written twice a year has no business crushing the
 * four things opened daily.
 *
 * Text, not icons: the studio has no icon set and inventing one would be the
 * first colour-and-picture chrome in an app that gets its hierarchy from
 * size and weight alone.
 */
export const THUMB_TABS: { view: StudioView; label: string; ownerOnly?: boolean }[] = [
  { view: 'none', label: 'Week' },
  { view: 'news', label: 'News' },
  { view: 'conversations', label: 'Dispatches', ownerOnly: true },
  { view: 'calendar', label: 'Calendar' },
]

export interface NavHandlers {
  view: StudioView
  showingCallSheets: boolean
  isOwner: boolean
  name: string
  onGoHome: () => void
  onNewProject: () => void
  onSelectView: (view: StudioView) => void
  onOpenTeam: () => void
  onOpenAccount: () => void
  onSignOut: () => void
}
